import { useSyncExternalStore } from 'react';
import { initI18n } from './init-react';
import { DEFAULT_LOCALE, isAppLocale, type AppLocale, type AppResources } from './index';

const STORAGE_KEY = 'tupai_language';

export type LanguageStorage = {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
};

// Storage must already be readable when this runs: the starting language is read once, synchronously.
export function createLanguageStore(storage: LanguageStorage, resources: AppResources) {
  function readStored(): AppLocale | null {
    const value = storage.getItem(STORAGE_KEY);
    return isAppLocale(value) ? value : null;
  }

  let language: AppLocale = readStored() ?? DEFAULT_LOCALE;
  const i18n = initI18n(language, resources);
  const listeners = new Set<() => void>();

  function apply(next: AppLocale) {
    language = next;
    storage.setItem(STORAGE_KEY, next);
    void i18n.changeLanguage(next);
    listeners.forEach((listener) => listener());
  }

  function getLanguage(): AppLocale {
    return language;
  }

  function setLanguage(next: AppLocale): void {
    if (next === language && readStored() === next) return;
    apply(next);
  }

  // A choice already saved on this device wins over the account's language.
  function hydrateLanguageFromServer(serverLang: string | null | undefined): void {
    if (!isAppLocale(serverLang) || readStored() !== null) return;
    apply(serverLang);
  }

  function subscribe(listener: () => void): () => void {
    listeners.add(listener);
    return () => listeners.delete(listener);
  }

  function useLanguage(): AppLocale {
    return useSyncExternalStore(subscribe, getLanguage, getLanguage);
  }

  function hasStoredLanguage(): boolean {
    return readStored() !== null;
  }

  return {
    i18n,
    getLanguage,
    setLanguage,
    hydrateLanguageFromServer,
    hasStoredLanguage,
    useLanguage,
  };
}
